import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const INITIAL_STATE = {
  // Theme
  currentTheme: 'light',

  // Sidebar
  sidebarExpanded: true,
  sidebarMobileOpen: false,

  // Loading states
  isLoading: false,
  loadingText: '',
  loadingProgress: 0,

  // Notifications
  notifications: [],

  // Modal states
  activeModals: new Set(),
  modalData: new Map(),

  // Breadcrumbs
  breadcrumbs: [],

  // View preferences
  viewMode: 'list',
  pageSize: 10,
  sortOrder: 'desc',
  sortField: 'createdAt',

  // Search
  searchTerm: '',
  searchFilters: {},

  // UI Preferences
  density: 'comfortable',
  animationsEnabled: true,
  confirmations: {
    deleteItems: true,
    cancelOperations: true,
    closeUnsaved: true,
  },
};

export const useUIStore = create(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      // Theme Management
      setTheme: (theme) => {
        if (theme === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
        set({ currentTheme: theme });
      },

      toggleTheme: () => {
        const newTheme = get().currentTheme === 'light' ? 'dark' : 'light';
        get().setTheme(newTheme);
      },

      // Sidebar Management
      toggleSidebar: () =>
        set((state) => ({
          sidebarExpanded: !state.sidebarExpanded,
        })),

      setSidebarExpanded: (expanded) =>
        set({
          sidebarExpanded: expanded,
        }),

      toggleMobileSidebar: () =>
        set((state) => ({
          sidebarMobileOpen: !state.sidebarMobileOpen,
        })),

      // Loading Management
      setLoading: (isLoading, text = '', progress = 0) =>
        set({
          isLoading,
          loadingText: text,
          loadingProgress: progress,
        }),

      updateLoadingProgress: (progress) =>
        set({
          loadingProgress: progress,
        }),

      // Notification Management
      addNotification: (notification) =>
        set((state) => ({
          notifications: [
            ...state.notifications,
            {
              id: `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Unique ID
              duration: 5000,
              type: 'info',
              ...notification,
            },
          ],
        })),

      removeNotification: (id) =>
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        })),

      clearNotifications: () =>
        set({
          notifications: [],
        }),

      // Modal Management
      openModal: (modalId, data = null) => {
        const { activeModals, modalData } = get();
        activeModals.add(modalId);
        if (data) modalData.set(modalId, data);
        set({
          activeModals: new Set(activeModals),
          modalData: new Map(modalData),
        });
      },

      closeModal: (modalId) => {
        const { activeModals, modalData } = get();
        activeModals.delete(modalId);
        modalData.delete(modalId);
        set({
          activeModals: new Set(activeModals),
          modalData: new Map(modalData),
        });
      },

      getModalData: (modalId) => get().modalData.get(modalId),

      // Breadcrumb Management
      setBreadcrumbs: (breadcrumbs) =>
        set({
          breadcrumbs,
        }),

      // View Management
      setViewMode: (mode) =>
        set({
          viewMode: mode,
        }),

      setPageSize: (size) =>
        set({
          pageSize: size,
        }),

      setSorting: (field, order = 'desc') =>
        set({
          sortField: field,
          sortOrder: order,
        }),

      // Search Management
      setSearchTerm: (term) =>
        set({
          searchTerm: term,
        }),

      setSearchFilters: (filters) =>
        set({
          searchFilters: filters,
        }),

      clearSearch: () =>
        set({
          searchTerm: '',
          searchFilters: {},
        }),

      // UI Preferences
      setDensity: (density) =>
        set({
          density,
        }),

      toggleAnimations: () =>
        set((state) => ({
          animationsEnabled: !state.animationsEnabled,
        })),

      updateConfirmation: (key, value) =>
        set((state) => ({
          confirmations: {
            ...state.confirmations,
            [key]: value,
          },
        })),

      // Quick Actions
      showSuccessMessage: (message) => {
        get().addNotification({
          type: 'success',
          message,
        });
      },

      showErrorMessage: (message) => {
        get().addNotification({
          type: 'error',
          message,
        });
      },

      showWarningMessage: (message) => {
        get().addNotification({
          type: 'warning',
          message,
        });
      },

      // Reset Store
      resetStore: () => {
        set(INITIAL_STATE);
      },

      // State Export/Import
      exportState: () => {
        const state = get();
        const exportable = { ...state };
        delete exportable.activeModals;
        delete exportable.modalData;
        return exportable;
      },

      importState: (state) => {
        set({
          ...INITIAL_STATE,
          ...state,
        });
      },
    }),
    {
      name: 'ui-storage',
      getStorage: () => localStorage,
      partialize: (state) => ({
        currentTheme: state.currentTheme,
        sidebarExpanded: state.sidebarExpanded,
        pageSize: state.pageSize,
        density: state.density,
        animationsEnabled: state.animationsEnabled,
        confirmations: state.confirmations,
      }),
    }
  )
);

// Initialize theme on load
const savedTheme = useUIStore.getState().currentTheme;
if (savedTheme === 'dark') {
  document.documentElement.classList.add('dark');
}

export default useUIStore;
